/**
 * MODULE: domain/pages/frontend/utils/crmEntityBrainRefreshUtils.ts
 * PURPOSE: Invalidate CRM entity brain context after email/postmark engagement signals.
 * DEPENDENCIES: crmBrainQueryUtils, pageBrainFocusUtils
 * PUBLIC_API: refreshCrmEntityBrainQueries, invalidateCrmEntityBrainQueries
 * SIDE_EFFECTS: Schedules timed query invalidations for fan-out lag
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 52 / 150 lines
 * RECENT: 2026-05-26 [2.2.1251] — activity bus notify on CRM entity engagement refresh.
 * RECENT: 2026-05-25 [2.2.1202] — Postmark/email opens refresh CRM drawer insights.
 */

import type { QueryClient } from '@tanstack/react-query';
import { notifyBrainActivityChanged } from '@shared/lib/frontend/utils/brainActivityEvents';
import { ENGAGEMENT_REFETCH_DELAYS_MS } from './crmBrainQueryUtils';
import type { PageBrainFocusKind } from './pageBrainFocusUtils';

export type CrmEntityBrainRefreshInput = {
  kind: PageBrainFocusKind;
  entityId: string;
};

function invalidateEntityBrainContext(
  qc: QueryClient,
  kind: PageBrainFocusKind,
  entityId: string,
): void {
  const id = entityId.trim();
  if (!id || kind === 'page') return;
  void qc.invalidateQueries({
    queryKey: ['pages', 'brain-context', kind, id],
  });
}

export function invalidateCrmEntityBrainQueries(
  qc: QueryClient,
  input: CrmEntityBrainRefreshInput,
): void {
  invalidateEntityBrainContext(qc, input.kind, input.entityId);
  void qc.invalidateQueries({ queryKey: ['pages', 'by-deal'] });
  void qc.invalidateQueries({ queryKey: ['pages', 'by-company'] });
  void qc.invalidateQueries({ queryKey: ['pages', 'by-contact'] });
}

export function refreshCrmEntityBrainQueries(
  qc: QueryClient,
  input: CrmEntityBrainRefreshInput,
): void {
  notifyBrainActivityChanged({
    source: 'poll',
    entity_type: input.kind === 'account' ? 'account' : input.kind,
    entity_id: input.entityId,
    scope: 'all',
  });
  for (const delayMs of ENGAGEMENT_REFETCH_DELAYS_MS) {
    if (delayMs === 0) {
      invalidateCrmEntityBrainQueries(qc, input);
      continue;
    }
    window.setTimeout(() => {
      invalidateCrmEntityBrainQueries(qc, input);
    }, delayMs);
  }
}
